/**
 * SWR 全域配置
 * 提供預設的 SWR 選項，適用於 Cloudflare Serverless 環境
 */

import { SWRConfiguration } from 'swr';

/**
 * SWR 預設配置
 *
 * 針對 Cloudflare Serverless 特性優化：
 * - revalidateOnFocus: 開啟以保持資料新鮮
 * - dedupingInterval: 避免短時間內重複請求
 * - keepPreviousData: 分頁/過濾場景避免 Loading 閃爍
 */
export const swrDefaultConfig: SWRConfiguration = {
  // 視窗焦點時重新驗證（預設開啟，適合 Cloudflare Serverless）
  revalidateOnFocus: true,

  // 重新連線時重新驗證
  revalidateOnReconnect: true,

  // 去重複請求間隔（2 秒內相同請求只發送一次）
  dedupingInterval: 2000,

  // 保持前一次資料（分頁/過濾場景避免 Loading 閃爍）
  keepPreviousData: true,

  // 錯誤重試配置
  shouldRetryOnError: error => {
    // 4xx 錯誤通常不重試，但 TOKEN_EXPIRED 可以重試一次（如果有 Refresh Token 機制）
    if (error && typeof error === 'object' && 'status' in error) {
      const errorObj = error as { status: number; code?: string };
      const status = errorObj.status;

      // 401 錯誤：檢查是否為 TOKEN_EXPIRED（可以重試一次）
      if (status === 401) {
        // 如果是 TOKEN_EXPIRED，可以重試一次（未來如果有 Refresh Token 機制）
        // 目前先不重試，因為沒有 Refresh Token 機制
        // if (errorObj.code === 'TOKEN_EXPIRED') {
        //   return true; // 允許重試一次
        // }
        return false; // 所有 401 錯誤目前都不重試
      }

      // 其他 4xx 錯誤不重試
      if (status >= 400 && status < 500) {
        return false;
      }
    }
    // 其他錯誤（5xx、網路錯誤等）最多重試 3 次
    return true;
  },

  // 重試次數
  errorRetryCount: 3,

  // 重試間隔（指數退避）- SWR 不支援函數，使用固定值
  errorRetryInterval: 5000,

  // 重新驗證間隔（可選，預設為 0 表示不自動重新驗證）
  refreshInterval: 0,
};

/**
 * 列表資料的 SWR 配置
 * 適用於需要即時更新的列表頁面
 */
export const swrListConfig: SWRConfiguration = {
  ...swrDefaultConfig,
  // 列表資料需要更頻繁的重新驗證
  revalidateOnFocus: true,
  dedupingInterval: 1000, // 1 秒
};

/**
 * 詳情資料的 SWR 配置
 * 適用於詳情頁面，不需要頻繁更新
 */
export const swrDetailConfig: SWRConfiguration = {
  ...swrDefaultConfig,
  // 詳情頁面可以關閉自動重新驗證
  revalidateOnFocus: false,
  dedupingInterval: 5000, // 5 秒
};
